import { useClerk, useUser } from '@clerk/clerk-react';
import { Analytics } from '@vercel/analytics/react';
import { UserIcon } from 'lucide-react';
import { CSSProperties, ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { AppSidebar } from '@/components/global/AppSidebar';
import { TourRenderer } from '@/components/tour/TourRenderer';
import { TriggerTour } from '@/components/tour/TriggerTour';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { buttonVariants } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { SidebarProvider, SidebarTrigger } from '@/components/ui/sidebar';
import { SIDEBAR_WIDTH } from '@/constants/ui/sidebar';
import { useTourManager } from '@/hooks/tour/useTourManager';
import { getFallbackURL } from '@/lib/route';
import { cn } from '@/lib/ui/utils';

interface MainLayoutProps {
  children: ReactNode;
}

export const MainLayout = ({ children }: MainLayoutProps) => {
  const { user } = useUser();
  const { signOut } = useClerk();
  const { currentTour, currentSteps, hasTour } = useTourManager();

  const handleLogOut = () => {
    window.localStorage.removeItem('access-token');
    signOut({
      redirectUrl: getFallbackURL(),
    });
  };

  const sidebarStyle = {
    '--sidebar-width': `${SIDEBAR_WIDTH}px`,
  } as CSSProperties;

  return (
    <SidebarProvider style={sidebarStyle}>
      <AppSidebar />
      <main className="h-screen flex-1">
        {hasTour && currentTour && (
          <TourRenderer
            steps={currentSteps}
            run={currentTour.run}
            stepIndex={currentTour.stepIndex}
            onCallback={currentTour.handleJoyrideCallback}
          />
        )}
        <header className="sticky top-0 z-10 flex items-center justify-between border-b border-alpha-dark-5 bg-white px-4 py-[15px] md:px-6">
          <div>
            <SidebarTrigger className="min-[920px]:hidden" />
          </div>
          <div className="flex items-center gap-3">
            {hasTour && currentTour && (
              <TriggerTour onStartTour={currentTour.startTour} />
            )}
            <DropdownMenu>
              <DropdownMenuTrigger
                asChild
                className={cn(
                  buttonVariants({
                    size: 'icon',
                    variant: 'ghost',
                    className:
                      'cursor-pointer overflow-hidden rounded-full shadow-md',
                  })
                )}
              >
                <Avatar className="tour-user-avatar size-9">
                  <AvatarImage src={user?.imageUrl} />
                  <AvatarFallback>
                    <UserIcon className="size-5" />
                  </AvatarFallback>
                </Avatar>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="mr-6 mt-2 w-[200px]">
                <Link to="/dashboard/profile?tab=general">
                  <DropdownMenuItem>
                    <p className="p-0.5">My Profile</p>
                  </DropdownMenuItem>
                </Link>
                <DropdownMenuItem onClick={handleLogOut}>
                  <p className="p-0.5">Sign Out</p>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        <div className="relative min-h-[calc(100vh-61px)] p-4 max-md:w-screen md:min-h-[calc(100vh-67px)] md:p-6 min-[920px]:w-[calc(100vw-230px)]">
          {children}
        </div>
      </main>
      <Analytics />
    </SidebarProvider>
  );
};
